export const veterinarySessionItems = [
	{
		type: "SET",
		stimulus: {
			id: 40198,
			content: {
				instruction:
					"Watch the video and select the correct answer from the options provided.",
				passage: {
					title: "Clinical Case: Canine Hindlimb Lameness",
					paragraphs: [
						{
							text: "A 7-year-old Labrador Retriever is brought in with a complaint of worsening right hindlimb lameness over the past several weeks.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi6H2tLYQjMM5slTlnJNoEFw",
						url: "vi6H2tLYQjMM5slTlnJNoEFw",
						type: "video",
						transcript:
							"He started limping on this back leg a few weeks ago… the right one. It's not getting better… he doesn't want to jump anymore. This leg looks smaller now… like he's not using it.",
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 40199,
				type: "MCSS",
				content: {
					parentId: 40198,
					parentType: "SET",
					stem: "Based on the dog's history and clinical presentation, what is the most appropriate initial diagnostic approach?",
					choices: [
						{
							id: "a1b2c3d4-5e6f-7a8b-9c0d-e1f2a3b4c5d6",
							stem: "Orthopedic examination with stifle radiographs",
							text: "Orthopedic examination with stifle radiographs",
							isCorrect: true,
							explanation: `An **orthopedic examination with stifle radiographs** is the most appropriate initial diagnostic approach for this case. The presentation of **progressive hindlimb lameness, reluctance to jump, and visible muscle atrophy** is consistent with **stifle (knee) joint pathology**, most commonly **cranial cruciate ligament (CCL) disease** in dogs. A thorough orthopedic examination—including **cranial drawer test and tibial thrust test**—combined with **lateral and craniocaudal radiographs of the stifle** provides the best initial assessment of joint stability, effusion, and secondary degenerative changes.

While other diagnostic options have their place, they are **not the preferred initial choice** in this scenario. **An empirical NSAID trial without further workup** delays diagnosis, masks symptoms, and risks missing a surgically correctable condition. **Abdominal ultrasound** is not indicated given the localized orthopedic presentation and absence of systemic signs. **A bloodwork-only panel** may be useful for pre-anesthetic screening or ruling out systemic disease, but it **does not address the primary musculoskeletal complaint** and would not guide treatment for this presentation.

For **a dog presenting with progressive unilateral hindlimb lameness and muscle atrophy**, a **focused orthopedic examination with stifle radiographs remains the gold standard first step.**

**Key Takeaways:**
- **Orthopedic examination with radiographs is the first-line diagnostic approach for suspected cruciate ligament disease** because it directly evaluates joint stability, effusion, and bony changes.
- **Empirical treatment without workup can delay appropriate intervention** and should be avoided when clinical signs point to a specific anatomical problem.

**References:**
- Kowaleski, M. P., Boudrieau, R. J., & Pozzi, A. (2018). *Stifle Joint*. In S. A. Johnston & K. M. Tobias (Eds.), Veterinary Surgery: Small Animal (2nd ed., pp. 1071–1168). Elsevier.
- Hayashi, K., Manley, P. A., & Muir, P. (2004). Cranial cruciate ligament pathophysiology in dogs: caudal cruciate ligament damage and cranial tibial subluxation. *Clinical Orthopaedics and Related Research*, 425, 253–258.
- Comerford, E. J., Smith, K., & Hayashi, K. (2011). Update on the aetiopathogenesis of canine cranial cruciate ligament disease. *Veterinary and Comparative Orthopaedics and Traumatology*, 24(02), 91–98.`,
							numFirstSelections: 138,
						},
						{
							id: "b2c3d4e5-6f7a-8b9c-0d1e-f2a3b4c5d6e7",
							stem: "Empirical NSAID trial without further workup",
							text: "Empirical NSAID trial without further workup",
							isCorrect: false,
							numFirstSelections: 36,
						},
						{
							id: "c3d4e5f6-7a8b-9c0d-1e2f-a3b4c5d6e7f8",
							stem: "Abdominal ultrasound",
							text: "Abdominal ultrasound",
							isCorrect: false,
							numFirstSelections: 18,
						},
						{
							id: "d4e5f6a7-8b9c-0d1e-2f3a-b4c5d6e7f8a9",
							stem: "Bloodwork-only panel (CBC and chemistry)",
							text: "Bloodwork-only panel (CBC and chemistry)",
							isCorrect: false,
							numFirstSelections: 28,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Orthopedic Diagnostics"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "a1b2c3d4-5e6f-7a8b-9c0d-e1f2a3b4c5d6",
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 40200,
			content: {
				instruction:
					"Watch the video and circle the abnormality visible in the image.",
				passage: {
					title: "Clinical Case: Canine Hindlimb Lameness",
					paragraphs: [
						{
							text: "A 7-year-old Labrador Retriever is brought in with a complaint of worsening right hindlimb lameness over the past several weeks.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi6H2tLYQjMM5slTlnJNoEFw",
						url: "vi6H2tLYQjMM5slTlnJNoEFw",
						type: "video",
						transcript:
							"He started limping on this back leg a few weeks ago… the right one. It's not getting better… he doesn't want to jump anymore. This leg looks smaller now… like he's not using it.",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "READING",
				title: "Integrated Marking Answer Stimulus",
				topics: ["Diagnostic Imaging"],
			},
		},
		items: [
			{
				id: 406575,
				versionId: 1,
				content: {
					parentId: 40200,
					parentType: "SET",
					stem: "Using the lateral radiograph of the right stifle provided, identify and circle the area showing pathology consistent with the dog's lameness. Your task is to annotate the region corresponding to the likely cause of the dog's symptoms.",
					choices: [],
					instruction:
						"Watch the video and circle the abnormality visible in the image.",
					media: [
						{
							id: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/thieme/normal_lateral.jpg",
							url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/thieme/normal_lateral.jpg",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description:
								"Improve your ability to precisely identify and mark abnormalities in medical imaging. Focus on developing keen attention to detail to ensure correct identification of pathological findings.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description:
								"Enhance your efficiency in analyzing and annotating medical images while maintaining diagnostic accuracy. Work on reducing annotation time without compromising the quality of your assessment.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Marking Answer",
					topics: ["Diagnostic Imaging"],
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 40203,
			content: {
				instruction:
					"Watch the video and arrange the steps in the correct order.",
				passage: {
					title: "Clinical Case: Canine Hindlimb Lameness",
					paragraphs: [
						{
							text: "A 7-year-old Labrador Retriever is brought in with a complaint of worsening right hindlimb lameness over the past several weeks.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi6H2tLYQjMM5slTlnJNoEFw",
						url: "vi6H2tLYQjMM5slTlnJNoEFw",
						type: "video",
						transcript:
							"He started limping on this back leg a few weeks ago… the right one. It's not getting better… he doesn't want to jump anymore. This leg looks smaller now… like he's not using it.",
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 40300,
				type: "MCOS",
				content: {
					parentId: 40203,
					parentType: "SET",
					stem: "Based on the dog's diagnosis of cranial cruciate ligament rupture with secondary muscle atrophy, arrange the following physiotherapy intervention steps in the correct clinical sequence.",
					choices: [
						{
							id: "e1a2b3c4-d5e6-1a2b-3c4d-5e6f7a8b9c0d",
							stem: "Passive range of motion",
							text: "Passive range of motion",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 48,
						},
						{
							id: "d0e1a2b3-c4d5-0e1a-2b3c-4d5e6f7a8b9c",
							stem: "Pain management and rest",
							text: "Pain management and rest",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 32,
						},
						{
							id: "f2a3b4c5-e6f7-2a3b-4c5d-6e7f8a9b0c1d",
							stem: "Controlled leash walks",
							text: "Controlled leash walks",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 41,
						},
						{
							id: "h4a5b6c7-a8b9-4a5b-6c7d-8e9f0a1b2c3d",
							stem: "Progressive strengthening exercises",
							text: "Progressive strengthening exercises",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 38,
						},
						{
							id: "g3a4b5c6-f7a8-3a4b-5c6d-7e8f9a0b1c2d",
							stem: "Hydrotherapy",
							text: "Hydrotherapy",
							media: [],
							isCorrect: false,
							explanation: null,
							numFirstSelections: 55,
						},
						{
							id: "i5a6b7c8-b9c0-5a6b-7c8d-9e0f1a2b3c4d",
							stem: "Return to full activity",
							text: "Return to full activity",
							media: [],
							isCorrect: true,
							explanation:
								"Rehabilitation following **cranial cruciate ligament (CCL) rupture** should follow a progressive, phased approach that respects tissue healing timelines and avoids overloading the injured joint.\n\nThe correct order is:\n1. **Pain management and rest** — Control inflammation and pain with NSAIDs, cryotherapy, and strict rest to allow initial tissue stabilization.\n2. **Passive range of motion** — Gentle joint mobilization prevents stiffness and maintains flexibility without placing weight-bearing stress on the stifle.\n3. **Controlled leash walks** — Short, slow leash walks reintroduce controlled weight bearing and encourage limb use while preventing uncontrolled activity.\n4. **Hydrotherapy** — Underwater treadmill or swimming provides low-impact, buoyancy-assisted exercise that builds muscle without excessive joint loading.\n5. **Progressive strengthening exercises** — Gradually increasing resistance activities such as incline walking, sit-to-stand exercises, and cavaletti rails rebuild quadriceps and hamstring strength.\n6. **Return to full activity** — Only after adequate muscle mass, joint stability, and pain-free movement are restored should unrestricted activity be permitted.\n\nPerforming steps out of order, such as introducing strengthening exercises before adequate pain control or attempting full activity before muscle recovery, increases the risk of re-injury and delayed healing.\n\n**Key Takeaways**\n- Always address pain and inflammation before introducing any exercise.\n- Progress from passive to active, low-impact to high-impact, in a controlled stepwise fashion.\n- Return to full activity is the final step and should only occur when clinical milestones are met.",
							numFirstSelections: 22,
						},
					],
					instruction: "Drag and drop each step into the correct sequence.",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				response: {
					maximumScore: 1,
					correctResponse: [
						{
							id: "d0e1a2b3-c4d5-0e1a-2b3c-4d5e6f7a8b9c", // Pain management and rest
						},
						{
							id: "e1a2b3c4-d5e6-1a2b-3c4d-5e6f7a8b9c0d", // Passive range of motion
						},
						{
							id: "f2a3b4c5-e6f7-2a3b-4c5d-6e7f8a9b0c1d", // Controlled leash walks
						},
						{
							id: "g3a4b5c6-f7a8-3a4b-5c6d-7e8f9a0b1c2d", // Hydrotherapy
						},
						{
							id: "h4a5b6c7-a8b9-4a5b-6c7d-8e9f0a1b2c3d", // Progressive strengthening exercises
						},
						{
							id: "i5a6b7c8-b9c0-5a6b-7c8d-9e0f1a2b3c4d", // Return to full activity
						},
					],
					numChoicesRequired: {
						max: 1,
						min: 1,
					},
				},
				metadata: {
					type: "MCOS",
					skill: "READING",
					title: "Sequence of Order",
					topics: ["Rehabilitation Protocol"],
				},
				score: 0,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 40201,
			content: {
				instruction:
					"Watch the video and write your answer in the text box provided.",
				passage: {
					title: "Clinical Case: Canine Hindlimb Lameness",
					paragraphs: [
						{
							text: "A 7-year-old Labrador Retriever is brought in with a complaint of worsening right hindlimb lameness over the past several weeks.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi6H2tLYQjMM5slTlnJNoEFw",
						url: "vi6H2tLYQjMM5slTlnJNoEFw",
						type: "video",
						transcript:
							"He started limping on this back leg a few weeks ago… the right one. It's not getting better… he doesn't want to jump anymore. This leg looks smaller now… like he's not using it.",
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Integrated Writing Stimulus",
				topics: ["Clinical Documentation"],
			},
		},
		items: [
			{
				id: 40657412,
				versionId: 1,
				content: {
					parentId: 40201,
					parentType: "SET",
					stem: "Using all prior case information and your findings, write a complete SOAP note documenting this encounter. Your note should clearly separate: Subjective (owner report, timeline, mechanism of injury), Objective (physical examination results, imaging findings), Assessment (diagnosis of cranial cruciate ligament rupture with secondary muscle atrophy and any differential diagnoses), and Plan (physiotherapy protocol, follow-up schedule, and referral considerations if the dog is a surgical candidate).",
					choices: [],
					instruction:
						"Watch the video and write your answer in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Comprehensiveness",
							icon: "file-document-outline",
							description:
								"Enhance your ability to create thorough documentation that captures all essential elements of patient encounters, ensuring no critical details are omitted from your written reports.",
						},
						{
							maxScore: 5,
							name: "Medical Necessity",
							icon: "clipboard-check-outline",
							description:
								"Strengthen your skills in providing clear and compelling justification for medical services, demonstrating how each intervention directly addresses patient needs and aligns with clinical standards.",
						},
						{
							maxScore: 5,
							name: "Medical Decision Making (MDM)",
							icon: "brain",
							description:
								"Develop stronger clinical reasoning abilities by improving how you document your diagnostic process, risk assessments, and treatment rationales in patient records.",
						},
						{
							maxScore: 5,
							name: "Code Alignment",
							icon: "barcode",
							description:
								"Improve your proficiency in selecting and applying appropriate billing codes that accurately capture the complexity and scope of medical services while ensuring compliance with coding guidelines.",
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Integrated Writing Response",
					topics: ["Clinical Documentation"],
					taskType: "default",
				},
				score: null,
			},
		],
	},
	{
		type: "SET",
		stimulus: {
			id: 40202,
			content: {
				instruction: "Watch the video and record your answer.",
				passage: {
					title: "Clinical Case: Canine Hindlimb Lameness",
					paragraphs: [
						{
							text: "A 7-year-old Labrador Retriever is brought in with a complaint of worsening right hindlimb lameness over the past several weeks.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "vi6H2tLYQjMM5slTlnJNoEFw",
						url: "vi6H2tLYQjMM5slTlnJNoEFw",
						type: "video",
						transcript:
							"He started limping on this back leg a few weeks ago… the right one. It's not getting better… he doesn't want to jump anymore. This leg looks smaller now… like he's not using it.",
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["Client Communication"],
			},
		},
		items: [
			{
				id: 405773434,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 40202,
					parentType: "SET",
					stem: `The owner is in the exam room, visibly worried, and has mentioned that money is tight. Verbally explain the dog's diagnosis in plain language, outline the rehabilitation plan, and present at least two cost-tiered options (e.g., full protocol with hydrotherapy vs. home-based exercise program with fewer clinic visits). Demonstrate empathetic tone, avoid jargon, acknowledge the financial concern directly, and ensure the owner feels supported rather than judged.`,
					choices: [],
					instruction: "Watch the video and record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description:
								"Demonstrate effective verbal and non-verbal communication skills while building rapport and maintaining professional boundaries with patients.",
						},
						{
							maxScore: 5,
							name: "Empathy & Patient-Centered Care",
							icon: "heart-outline",
							description:
								"Show genuine understanding of patient concerns while providing individualized care that respects their values, preferences and needs.",
						},
						{
							maxScore: 5,
							name: "Counseling & Patient Education",
							icon: "human-male-board",
							description:
								"Effectively educate patients about their condition, treatment options, and self-care strategies using clear, accessible language.",
						},
						{
							maxScore: 5,
							name: "Professionalism & Ethical Conduct",
							icon: "briefcase-outline",
							description:
								"Maintain professional demeanor and ethical standards while demonstrating cultural competence and respect for patient autonomy.",
						},
						{
							maxScore: 5,
							name: "Organization & Logical Flow",
							icon: "file-tree-outline",
							description:
								"Present information in a clear, structured manner with smooth transitions between topics and appropriate pacing.",
						},
						{
							maxScore: 5,
							name: "Closure & Next Steps",
							icon: "arrow-right-circle-outline",
							description:
								"Effectively summarize key points, verify patient understanding, and clearly communicate follow-up plans and expectations.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Input Item",
					topics: ["Client Communication"],
				},
				score: 0,
			},
		],
	},
];
